import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppStateContext } from '../../contexts/AppStateContext';
import { AuthContext } from '../../contexts/AuthContext';
import { BannerContext } from '../../contexts/BannerContext';
import { StreamsContext } from '../../contexts/StreamsContext';
import { useTheme } from '../../contexts/ThemeContext';
import { TradingContext } from '../../contexts/TradingContext';
import useIsMobile from '../../hooks/useIsMobile';
import usePositionCalculations from '../../hooks/usePositionCalculations';
import { useCancelOrder, useCreateOrder, useGetTicker } from '../../hooks/useTradeStation';
import { generateOrderWithFlexibleBracket } from '../../services/tradestationApi';
import {
  cancelOrders,
  getOpenOrders,
  getOpenStopLossOrders,
  getOpenTakeProfitOrders,
} from '../../services/tradestationUtils';
import { isBrokerLinked } from '../../utils/brokerConnection';
import { parseCreateOrderFailure } from '../../utils/orderResultUtils';
import { formatSignedCurrency, formatSignedPercent } from '../../utils/plFormat';
import { positionOpenedMs } from '../../utils/canonicalFields';
import { canOpenTradeWithSymbol, navigateToTradeWithSymbol } from '../../utils/tradeNavigation';
import { getTickerMetadata } from '../../utils/tickerSearch';
import { buildPositionsOverviewStats } from '../../utils/positionsOverviewStats';
import PositionMiniChart from '../StockChart/PositionMiniChart';
import PositionSizeOrderModal from './PositionSizeOrderModal';
import PositionsOverviewSummaryCard from './PositionsOverviewSummaryCard';

const matchesPositionQuery = (position, query) => {
  const symbol = String(position.Symbol || '').toUpperCase();
  if (symbol.includes(query)) return true;
  const companyName = String(
    getTickerMetadata(position.Symbol)?.name || position.Description || ''
  ).toUpperCase();
  return companyName.includes(query);
};

const positionCostBasis = (position) => {
  const avg = parseFloat(position?.AveragePrice) || 0;
  const qty = Math.abs(parseFloat(position?.Quantity) || 0);
  return avg * qty;
};

const positionTodaysPL = (position) => {
  const value = parseFloat(position?.TodaysProfitLoss);
  return Number.isFinite(value) ? value : 0;
};

const positionMarketValue = (position) => {
  const price = parseFloat(position?.MarkToMarketPrice) || parseFloat(position?.Last) || 0;
  const qty = Math.abs(parseFloat(position?.Quantity) || 0);
  return price * qty;
};

const formatPrice = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? `$${parsed.toFixed(2)}` : '—';
};

const ProtectionPill = ({ label, active, price, colors }) => (
  <span
    title={active ? `${label} @ ${formatPrice(price)}` : `No ${label === 'SL' ? 'stop loss' : 'take profit'} order`}
    style={{
      padding: '2px 6px',
      borderRadius: 4,
      backgroundColor: active ? 'rgba(7, 203, 163, 0.15)' : `${colors.border}88`,
      color: active ? (colors.chartUp || colors.success) : colors.textMuted,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.03em',
    }}
  >
    {label}
  </span>
);

const CHART_HEIGHT = 300;

const daysOpenLabel = (position) => {
  const openedMs = positionOpenedMs(position);
  if (!openedMs) return '—';
  const days = Math.max(0, Math.floor((Date.now() - openedMs) / 86400000));
  if (days === 0) return 'Today';
  return days === 1 ? '1 day' : `${days} days`;
};

const accountShareLabel = (marketValue, accountEquity) => {
  if (!(accountEquity > 0) || !Number.isFinite(marketValue)) return '—';
  return `${((Math.abs(marketValue) / accountEquity) * 100).toFixed(2)}%`;
};

const PositionChartFacts = ({ position, marketValue, accountEquity, stopDistance, rMultiple, colors }) => {
  const facts = [
    ['Avg', formatPrice(position.AveragePrice)],
    ['Value', formatSignedCurrency(Math.abs(marketValue)).replace(/^\+/, '')],
    ['Open', daysOpenLabel(position)],
    ['Account', accountShareLabel(marketValue, accountEquity)],
  ];
  if (stopDistance != null) facts.push(['To stop', formatPrice(stopDistance)]);
  if (rMultiple != null) facts.push(['R', rMultiple]);

  return (
    <div
      data-testid={`position-chart-facts-${position.Symbol}`}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px 14px',
        padding: '8px 12px 10px',
        borderBottom: `1px solid ${colors.border}`,
        backgroundColor: colors.bgSecondary,
        fontSize: 11,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {facts.map(([label, valueText]) => (
        <span key={label} style={{ display: 'inline-flex', gap: 5, alignItems: 'baseline' }}>
          <span style={{ color: colors.textMuted }}>{label}</span>
          <strong style={{ color: colors.text }}>
            {valueText}
          </strong>
        </span>
      ))}
    </div>
  );
};

/** Compact Buy / Sell pill; green for buy, red for sell. */
const SizeActionButton = ({ label, ariaLabel, color, disabled, onClick }) => (
  <button
    type="button"
    title={ariaLabel || label}
    aria-label={ariaLabel || label}
    disabled={disabled}
    onClick={onClick}
    style={{
      minWidth: 58,
      height: 32,
      flexShrink: 0,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 12px',
      border: 'none',
      borderRadius: 8,
      backgroundColor: color,
      color: '#FFFFFF',
      fontSize: 13,
      fontWeight: 800,
      letterSpacing: '0.02em',
      cursor: disabled ? 'default' : 'pointer',
      opacity: disabled ? 0.4 : 1,
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.28)',
    }}
  >
    {label}
  </button>
);

function tradeSideForAction(action, position) {
  const isShort = String(position?.LongShort || '').toLowerCase() === 'short';
  if (action === 'add') return isShort ? 'Sell' : 'Buy';
  return isShort ? 'Buy' : 'Sell';
}

const isExitOrderForPosition = (order, position) => {
  const action = String(order?.TradeAction || order?.Legs?.[0]?.BuyOrSell || '').toUpperCase();
  if (!action) return true;
  if (position?.LongShort === 'Short') return action === 'BUY' || action === 'BUYTOCOVER';
  return action === 'SELL';
};

const PositionCard = ({
  position,
  orders,
  interval,
  unit,
  canTrade,
  onOrder,
  onOpenSymbol,
  accountEquity,
  chartType = 'line',
}) => {
  const { colors } = useTheme();
  const appState = useContext(AppStateContext);
  const isMobile = useIsMobile();
  const { calculateMarketValue, calculateUnrealizedPL } = usePositionCalculations();
  const containerRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [revealActions, setRevealActions] = useState(false);
  const showSizeActions = isMobile || revealActions;
  const absQty = Math.abs(parseFloat(position?.Quantity) || 0);
  const symbolOpensTrade = typeof onOpenSymbol === 'function';
  const pnl = calculateUnrealizedPL(position);
  const today = positionTodaysPL(position);
  const marketValue = calculateMarketValue(position);
  const valueBasis = Math.abs(Number(marketValue)) || 0;
  const pnlPercent = valueBasis > 0 ? (pnl / valueBasis) * 100 : null;
  const todayPercent = valueBasis > 0 ? (today / valueBasis) * 100 : null;
  const risk = Number(appState?.risk) || 0;
  const rMultiple = risk > 0 ? `${(pnl / risk).toFixed(2)} R` : null;
  const lastPrice = parseFloat(position?.Last) || parseFloat(position?.MarkToMarketPrice);
  const stopPrice = parseFloat(orders?.stopLosses?.[0]?.StopPrice);
  const stopDistance = Number.isFinite(lastPrice) && Number.isFinite(stopPrice)
    ? Math.abs(lastPrice - stopPrice)
    : null;
  const plColor = (value) => (
    value > 0 ? colors.chartUp : value < 0 ? colors.chartDown : colors.text
  );

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { rootMargin: '200px' } // Load before entering viewport
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} style={{ minWidth: 0, width: '100%' }}>
      <div
        data-testid={`position-card-${position.Symbol}`}
        onMouseEnter={() => setRevealActions(true)}
        onMouseLeave={() => setRevealActions(false)}
        onFocusCapture={() => setRevealActions(true)}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget)) {
            setRevealActions(false);
          }
        }}
        style={{
          backgroundColor: colors.bgCard,
          border: `1px solid ${colors.border}`,
          borderRadius: 10,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'relative',
            minHeight: 58,
            padding: '10px 12px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <div style={{ minWidth: 84 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <strong
                role={symbolOpensTrade ? 'button' : undefined}
                tabIndex={symbolOpensTrade ? 0 : undefined}
                title={symbolOpensTrade ? `Open ${position.Symbol} on Trade` : position.Symbol}
                onClick={symbolOpensTrade ? () => onOpenSymbol(position.Symbol) : undefined}
                onKeyDown={symbolOpensTrade ? (event) => {
                  if (event.key !== 'Enter' && event.key !== ' ') return;
                  event.preventDefault();
                  onOpenSymbol(position.Symbol);
                } : undefined}
                style={{
                  color: symbolOpensTrade ? colors.primary : colors.text,
                  fontSize: 16,
                  cursor: symbolOpensTrade ? 'pointer' : 'default',
                }}
              >
                {position.Symbol}
              </strong>
              <ProtectionPill
                label="SL"
                active={orders.stopLosses.length > 0}
                price={orders.stopLosses[0]?.StopPrice}
                colors={colors}
              />
              <ProtectionPill
                label="TP"
                active={orders.takeProfits.length > 0}
                price={orders.takeProfits[0]?.LimitPrice}
                colors={colors}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 5 }}>
              <span style={{ color: colors.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: '0.05em' }}>
                {position.LongShort?.toUpperCase?.() || ''}
              </span>
              <span
                data-testid={`position-qty-${position.Symbol}`}
                style={{
                  color: colors.text,
                  fontSize: 16,
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {absQty}
              </span>
            </div>
          </div>

          <div style={{ marginLeft: 'auto' }} />
          {showSizeActions ? (
            <div
              data-testid={`position-size-actions-${position.Symbol}`}
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                zIndex: 2,
              }}
            >
              {['Buy', 'Sell'].map((side) => {
                const action = tradeSideForAction('add', position) === side ? 'add' : 'reduce';
                return (
                  <SizeActionButton
                    key={side}
                    label={side}
                    ariaLabel={`${side} ${position.Symbol}`}
                    color={side === 'Buy' ? colors.chartUp : colors.chartDown}
                    disabled={!canTrade}
                    onClick={() => onOrder(action, position)}
                  />
                );
              })}
            </div>
          ) : null}

          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>
            {[
              ['Today', today, todayPercent, `position-today-${position.Symbol}`],
              ['P&L', pnl, pnlPercent, `position-pnl-${position.Symbol}`],
            ].map(([label, value, percent, testId]) => (
              <div key={label} data-testid={testId} style={{ minWidth: 72, textAlign: 'right' }}>
                <div style={{ color: colors.textMuted, fontSize: 10, fontWeight: 600 }}>{label}</div>
                <strong style={{ color: plColor(value), fontSize: 14 }}>
                  {formatSignedCurrency(value)}
                </strong>
                <div title="of market value" style={{ color: plColor(percent), fontSize: 11, fontWeight: 650 }}>
                  {percent == null ? '—' : formatSignedPercent(percent)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <PositionChartFacts
          position={position}
          marketValue={marketValue}
          accountEquity={accountEquity}
          stopDistance={stopDistance}
          rMultiple={rMultiple}
          colors={colors}
        />

        <div style={{ position: 'relative', height: CHART_HEIGHT }}>
          {isVisible ? (
            <PositionMiniChart
              symbol={position.Symbol}
              position={position}
              height={CHART_HEIGHT}
              hideMeta
              interval={interval}
              unit={unit}
              showPositionLine
              chartType={chartType}
              exitLines={[
                ...orders.stopLosses.map((order) => ({
                  price: order.StopPrice,
                  color: colors.chartDown,
                  title: 'SL',
                })),
                ...orders.takeProfits.map((order, index) => ({
                  price: order.LimitPrice,
                  color: colors.chartUp,
                  title: orders.takeProfits.length > 1 ? `TP${index + 1}` : 'TP',
                })),
              ]}
            />
          ) : (
            <div style={{
              height: CHART_HEIGHT,
              backgroundColor: colors.bgCard,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: colors.textMuted,
            }}>
              <div style={{
                width: 32,
                height: 32,
                border: `3px solid ${colors.border}`,
                borderTopColor: colors.primary,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }} />
            </div>
          )}
        </div>
      </div>
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

const MarketPositionsPage = ({ interval = 1, unit = 'Daily', symbolQuery = '', chartType = 'line', toolbar = null }) => {
  const { colors } = useTheme();
  const { user } = useContext(AuthContext);
  const { showMessage } = useContext(BannerContext);
  const { quotesBySymbol } = useContext(StreamsContext);
  const {
    positions,
    orders,
    balances,
    selectedAccount,
    isPaperTrading,
  } = useContext(TradingContext);
  const isMobile = useIsMobile();
  const { calculateUnrealizedPL } = usePositionCalculations();
  const navigate = useNavigate();
  const { createOrder } = useCreateOrder();
  const { cancelOrder } = useCancelOrder();
  const { getTicker } = useGetTicker();
  const [orderIntent, setOrderIntent] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Symbol clicks open Trade when any linked connection has market data.
  const symbolOpensTrade = canOpenTradeWithSymbol(user, selectedAccount);
  const handleOpenSymbol = useCallback((symbol) => {
    navigateToTradeWithSymbol(navigate, symbol, user, selectedAccount);
  }, [navigate, selectedAccount, user]);

  const openPositions = useMemo(() =>
    (positions || [])
      .filter((p) => Math.abs(parseFloat(p.Quantity) || 0) > 0)
      .sort((a, b) => String(a.Symbol || '').localeCompare(String(b.Symbol || ''))),
    [positions]
  );
  const normalizedQuery = String(symbolQuery || '').trim().toUpperCase();
  const visiblePositions = useMemo(() => {
    if (!normalizedQuery) return openPositions;
    return openPositions.filter((position) => matchesPositionQuery(position, normalizedQuery));
  }, [normalizedQuery, openPositions]);

  const ordersBySymbol = useMemo(() => {
    return openPositions.reduce((acc, position) => {
      const open = getOpenOrders(orders || [], position.Symbol, selectedAccount);
      acc[position.Symbol] = {
        open,
        stopLosses: getOpenStopLossOrders(open, position.Symbol)
          .filter((order) => isExitOrderForPosition(order, position)),
        takeProfits: getOpenTakeProfitOrders(open, position.Symbol)
          .filter((order) => isExitOrderForPosition(order, position)),
      };
      return acc;
    }, {});
  }, [openPositions, orders, selectedAccount]);

  const accountEquity = useMemo(() => {
    const balance = (balances || []).find((row) => row.AccountID === selectedAccount?.AccountID);
    const equity = parseFloat(balance?.Equity);
    return Number.isFinite(equity) && equity > 0 ? equity : null;
  }, [balances, selectedAccount]);

  const overviewStats = useMemo(() => {
    const missingStopCount = openPositions.filter(
      (position) => !(ordersBySymbol[position.Symbol]?.stopLosses?.length),
    ).length;
    const withTakeProfitCount = openPositions.filter(
      (position) => ordersBySymbol[position.Symbol]?.takeProfits?.length,
    ).length;
    return buildPositionsOverviewStats(openPositions, calculateUnrealizedPL, missingStopCount, {
      getCostBasis: positionCostBasis,
      getTodaysPL: positionTodaysPL,
      getMarketValue: positionMarketValue,
      accountEquity,
      withTakeProfitCount,
    });
  }, [accountEquity, calculateUnrealizedPL, openPositions, ordersBySymbol]);

  const handleSubmit = useCallback(async ({ quantity, orderType, limitPrice, timeInForce }) => {
    const action = orderIntent?.action;
    const position = orderIntent?.position;
    if (!action || !position || !selectedAccount) return;

    setSubmitting(true);
    try {
      let tickerDetails = null;
      try {
        const response = await getTicker(position.Symbol);
        tickerDetails = response?.Symbols?.[0] || null;
      } catch (_) {}

      const isFutures = position.AssetType?.toUpperCase?.() === 'FUTURE'
        || selectedAccount.AccountType === 'Futures';
      const orderDirection = action === 'add'
        ? position.LongShort
        : (position.LongShort === 'Long' ? 'Short' : 'Long');
      const quotePrice = parseFloat(
        quotesBySymbol?.[position.Symbol]?.Last
        || position.Last
        || position.MarkToMarketPrice
      );
      const payload = generateOrderWithFlexibleBracket(
        selectedAccount,
        position.Symbol,
        quantity,
        null,
        orderDirection,
        orderType === 'limit' ? limitPrice : null,
        'none',
        0,
        isFutures,
        position,
        tickerDetails,
        Number.isFinite(quotePrice) ? quotePrice : null,
        orderType,
        timeInForce || 'DAY',
      );

      const positionOrders = ordersBySymbol[position.Symbol]?.open || [];
      if (action === 'reduce' && positionOrders.length > 0) {
        const cancellation = await cancelOrders(
          isPaperTrading,
          positionOrders,
          cancelOrder,
          showMessage,
        );
        if (cancellation.errorCount > 0) {
          throw new Error('Some open orders could not be canceled. The reduction was not submitted.');
        }
      }

      const result = await createOrder(isPaperTrading, payload);
      const failure = parseCreateOrderFailure(result);
      if (failure) {
        showMessage(`Couldn't ${action} ${position.Symbol}: ${failure}`, 'warning', true, 'banner');
        return;
      }

      showMessage(
        `${action === 'add' ? 'Add' : 'Reduce'} order submitted for ${quantity} ${position.Symbol}`,
        'success',
        true,
        'notification',
      );
      setOrderIntent(null);
    } catch (error) {
      showMessage(
        `Couldn't ${orderIntent?.action || 'update'} ${orderIntent?.position?.Symbol || 'position'}: ${error?.message || 'Unknown error'}`,
        'warning',
        true,
        'banner',
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    cancelOrder,
    createOrder,
    getTicker,
    isPaperTrading,
    orderIntent,
    ordersBySymbol,
    quotesBySymbol,
    selectedAccount,
    showMessage,
  ]);

  if (openPositions.length === 0) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 48,
        color: colors.textMuted,
        minHeight: 300,
      }}>
        <div style={{ fontSize: 16, fontWeight: 600 }}>No open positions</div>
        <div style={{ fontSize: 13, marginTop: 8 }}>
          Position charts will appear here once you open a trade.
        </div>
      </div>
    );
  }

  if (visiblePositions.length === 0) {
    return (
      <>
        <PositionsOverviewSummaryCard stats={overviewStats} />
        {toolbar}
        <div
          data-testid="positions-monitor-empty-filter"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 48,
            color: colors.textMuted,
            minHeight: 300,
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 600 }}>No matching positions</div>
          <div style={{ fontSize: 13, marginTop: 8 }}>
            Nothing matches “{symbolQuery.trim()}”.
          </div>
        </div>
      </>
    );
  }

  const activePosition = orderIntent?.position;
  const activeOrders = activePosition ? ordersBySymbol[activePosition.Symbol]?.open || [] : [];
  const activeLastPrice = activePosition
    ? quotesBySymbol?.[activePosition.Symbol]?.Last
      || activePosition.Last
      || activePosition.MarkToMarketPrice
    : null;

  return (
    <>
      <PositionsOverviewSummaryCard stats={overviewStats} />
      {toolbar}
      <div
        data-testid="positions-monitor-grid"
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${isMobile ? 1 : 2}, minmax(0, 1fr))`,
          gap: 16,
          paddingBottom: 24,
        }}
      >
        {visiblePositions.map(position => (
          <PositionCard
            key={`${position.AccountID}-${position.Symbol}`}
            position={position}
            orders={ordersBySymbol[position.Symbol]}
            interval={interval}
            unit={unit}
            canTrade={isBrokerLinked(user) && Boolean(selectedAccount?.AccountID)}
            onOrder={(action, selectedPosition) => setOrderIntent({ action, position: selectedPosition })}
            onOpenSymbol={symbolOpensTrade ? handleOpenSymbol : null}
            accountEquity={accountEquity}
            chartType={chartType}
          />
        ))}
      </div>
      {orderIntent ? (
        <PositionSizeOrderModal
          show
          action={orderIntent.action}
          position={activePosition}
          lastPrice={activeLastPrice}
          hasProtectiveOrders={activeOrders.length > 0}
          submitting={submitting}
          onHide={() => !submitting && setOrderIntent(null)}
          onSubmit={handleSubmit}
        />
      ) : null}
    </>
  );
};

export default MarketPositionsPage;
